import React, {PropsWithChildren, useEffect, useRef, useState} from "react";
import styled from "styled-components";

const FadeInOutBox = styled.div<{duration?: number; opacity: number;}>`
    transition: opacity ${({duration}) => duration}ms ease-in-out;
    opacity: ${({opacity}) => opacity};
`;

interface IFadeInOnMountProps {
    className?: string;
    delay?: number;
    duration?: number;
    onFadeInComplete?(): void;
}

export const FadeInOnMount: React.FC<PropsWithChildren<IFadeInOnMountProps>> = (props) => {
    let ref = useRef<HTMLDivElement>(null);
    let [opacity, setOpacity] = useState(0);
    useEffect(() => {
        // This line avoids this problem:
        // The ref value 'ref.current' will likely have changed by the time this effect cleanup function runs.
        // If this ref points to a node rendered by React, copy 'ref.current' to a variable inside the effect, and use that variable in the cleanup function
        let domElement = ref.current;
        domElement.addEventListener("transitionend", props.onFadeInComplete);
        return () => {
            domElement.removeEventListener("transitionend", props.onFadeInComplete);
        }
    }, [props.onFadeInComplete]);
    useEffect(() => {
        let timeOut = setTimeout(() => {
            setOpacity(1);
        }, props.delay || 0);
        return () => { clearTimeout(timeOut); }
    }, [props.delay]);
    return <FadeInOutBox ref={ref} className={props.className} duration={props.duration || 300} opacity={opacity}>{props.children}</FadeInOutBox>
}


interface IFadeWrapperProps {
    className?: string;
    duration?: number;
    delay?: number
    show: boolean;
    onFadeInComplete?(): void;
    onFadeOutComplete?(): void;
}

export const FadeWrapper: React.FC<PropsWithChildren<IFadeWrapperProps>> = (props) => {
    let ref = useRef<HTMLDivElement>(null);
    // i use a `show` state instead of the prop because on mount we want to render it invisible and
    // then we want to change the `show` state to change the opacity so it would fade in.
    // Otherwise the component will directly appear
    let [show, setShow] = useState(false);

    useEffect(() => {
        // This line avoids this problem:
        // The ref value 'ref.current' will likely have changed by the time this effect cleanup function runs.
        // If this ref points to a node rendered by React, copy 'ref.current' to a variable inside the effect, and use that variable in the cleanup function
        let domElement = ref.current;
        let callback = props.show ? props.onFadeInComplete : props.onFadeOutComplete;
        domElement.addEventListener("transitionend", callback);
        return () => {
            domElement.removeEventListener("transitionend", callback);
        }
    }, [props.show, props.onFadeInComplete, props.onFadeOutComplete]);

    useEffect(() => {
        let timeOut = setTimeout(() => { setShow(props.show); }, props.delay);
        return () => {
            clearTimeout(timeOut);
        }
    }, [props.show, props.delay]);

    let opacity = show ? 1 : 0;
    return <FadeInOutBox ref={ref} className={props.className} duration={props.duration || 300} opacity={opacity}>{props.children}</FadeInOutBox>
}